import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { Alert } from '../types';
import { useOrgStore } from './org-store.js';

// QG-CLI-COMP-004: the finance-domain stores (Market/Trading/Strategy/Portfolio)
// that used to live in this file now live in `modules/finance/client/store.ts`.
// `useAlertStore` and `useUIStore` below stay here — `useAlertStore` is consumed
// by core `components/Layout.tsx`, and its `Alert` type carries general system
// notifications, not just finance ones.

// ==========================================
// Auth Store
// ==========================================

type UserStatus = 'pending' | 'approved' | 'rejected';

/** Server-persisted `users.settings` blob. Open-ended — only the keys the client reads are named. */
export type AuthUserSettings = { uiMode?: 'simple' | 'detailed'; activeModuleId?: string } & Record<string, unknown>;

interface AuthUser {
  id: string;
  email: string;
  username?: string;
  fullName?: string;
  status?: UserStatus;
  organizationId: number;
  organizationName?: string | null;
  organizationPlan?: string | null;
  permissions?: string[];
  /** Server-persisted user settings. `uiMode` picks the simplified vs detailed shell. */
  settings?: AuthUserSettings;
}

interface AuthState {
  /** Signed in as far as this tab knows. After a reload the access token is re-obtained from the session cookie. */
  isAuthenticated: boolean;
  /**
   * The access token: kept in memory only, never persisted. The refresh token is not in the client at all —
   * it lives in the httpOnly `cf_refresh` cookie, out of reach of page script.
   */
  token: string | null;
  user: AuthUser | null;
  login: (token: string, user: AuthUser) => void;
  setToken: (token: string) => void;
  logout: () => void;
  updatePermissions: (permissions: string[]) => void;
  updateSettings: (patch: Record<string, unknown>) => void;
  isAdmin: () => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
}

/** What survives a reload: the profile, never a credential. */
type PersistedAuth = Pick<AuthState, 'isAuthenticated' | 'user'>;

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        isAuthenticated: false,
        token: null,
        user: null,
        // QG-CLI-COMP-003: team scoping belongs to an identity. `activeTeamId` is persisted
        // in localStorage and injected as `X-Team-Id` on every request, so carrying it across
        // an identity change makes the server 403 every policied route. Clear it on both
        // edges — login covers the account-switch path, which calls login() with the new user.
        login: (token, user) => {
          useOrgStore.getState().clearTeamContext();
          set({ isAuthenticated: true, token, user });
        },
        setToken: (token) => set({ token }),
        logout: () => {
          useOrgStore.getState().clearTeamContext();
          set({ isAuthenticated: false, token: null, user: null });
        },
        updatePermissions: (permissions) =>
          set((state) => ({
            user: state.user ? { ...state.user, permissions } : null,
          })),
        updateSettings: (patch) =>
          set((state) => ({
            user: state.user ? { ...state.user, settings: { ...(state.user.settings ?? {}), ...patch } } : null,
          })),
        isAdmin: () => {
          const perms = get().user?.permissions ?? [];
          return perms.includes('manage:users');
        },
        hasPermission: (permission: string) => {
          const perms = get().user?.permissions ?? [];
          return perms.includes(permission);
        },
        hasAnyPermission: (...permissions: string[]) => {
          const perms = get().user?.permissions ?? [];
          return permissions.some((p) => perms.includes(p));
        },
      }),
      {
        name: 'auth-storage',
        // zustand 5's default storage is `window.localStorage`; the global one is what zustand 4 used and what the tests stub.
        storage: createJSONStorage(() => localStorage),
        // Version 1 (2026-10-16): credentials left localStorage. Earlier versions stored the access and
        // refresh tokens; migrating drops them and signs the tab out, because the refresh token now has to
        // come from the httpOnly cookie that only a fresh sign-in sets.
        version: 1,
        partialize: (state): PersistedAuth => ({ isAuthenticated: state.isAuthenticated, user: state.user }),
        migrate: (): PersistedAuth => ({ isAuthenticated: false, user: null }),
      },
    ),
    { name: 'auth-store' },
  ),
);

// ==========================================
// Store Types
// ==========================================

interface AlertState {
  alerts: Alert[];
  unreadCount: number;
  addAlert: (alert: Alert) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
}

interface UIState {
  sidebarOpen: boolean;
  orderPanelOpen: boolean;
  workflowBuilderLayout: 'classic' | 'tabs';
  /** The sidebar's "Simple view" card is an introduction: once the person has opened the simple view it becomes a row. */
  simpleViewIntroSeen: boolean;
  /** The simple view's "In this conversation" column is open beside the conversation. */
  conversationRecordsOpen: boolean;
  toggleSidebar: () => void;
  setConversationRecordsOpen: (open: boolean) => void;
  toggleOrderPanel: () => void;
  setWorkflowBuilderLayout: (mode: 'classic' | 'tabs') => void;
  markSimpleViewIntroSeen: () => void;
}

// ==========================================
// Alert Store
// ==========================================

export const useAlertStore = create<AlertState>()(
  devtools(
    (set) => ({
      alerts: [],
      unreadCount: 0,

      addAlert: (alert) =>
        set((state) => ({
          alerts: [alert, ...state.alerts].slice(0, 100),
          unreadCount: state.unreadCount + 1,
        })),

      markAsRead: (id) =>
        set((state) => {
          const alert = state.alerts.find((a) => a.id === id);
          if (!alert || alert.read) return state;
          return {
            alerts: state.alerts.map((a) => (a.id === id ? { ...a, read: true } : a)),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),

      markAllAsRead: () =>
        set((state) => ({
          alerts: state.alerts.map((a) => ({ ...a, read: true })),
          unreadCount: 0,
        })),

      removeAlert: (id) =>
        set((state) => {
          const alert = state.alerts.find((a) => a.id === id);
          return {
            alerts: state.alerts.filter((a) => a.id !== id),
            unreadCount: alert && !alert.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        }),

      clearAlerts: () => set({ alerts: [], unreadCount: 0 }),
    }),
    { name: 'alert-store' },
  ),
);

// ==========================================
// UI Store (Persisted)
// ==========================================
// The color theme is not here: next-themes owns it (`data-theme` on <html>, see main.tsx).

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        sidebarOpen: true,
        orderPanelOpen: true,
        workflowBuilderLayout: 'classic',
        simpleViewIntroSeen: false,
        conversationRecordsOpen: false,

        toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

        setConversationRecordsOpen: (open) => set({ conversationRecordsOpen: open }),

        toggleOrderPanel: () => set((state) => ({ orderPanelOpen: !state.orderPanelOpen })),

        setWorkflowBuilderLayout: (mode) => set({ workflowBuilderLayout: mode }),

        markSimpleViewIntroSeen: () => set({ simpleViewIntroSeen: true }),
      }),
      { name: 'ui-settings', storage: createJSONStorage(() => localStorage) },
    ),
    { name: 'ui-store' },
  ),
);
